import { alertTypePresentation, type AlertPresentationTone } from '@traintopia/domain'
import { Colors, isSubwayRoute } from './colors'
import type { ServiceAlert } from '../store/transit'

// ─────────────────────────────────────────────────────────────────────────────
// Service-alert presentation.
//
// MTA populates the standard GTFS-RT `effect` field on not one alert it publishes. The real
// classification is its own `mercury_alert.alert_type`, which the backend normalises onto
// `alertType` (+ `planned` for the "Planned - " prefix) — that is what this keys off.
//
// `effect` is still honoured as a fallback, so a feed that does set it, or a cached alert
// from before the backend switched sources, renders sensibly rather than as "Unknown".
// ─────────────────────────────────────────────────────────────────────────────

interface Presentation {
  label: string
  /** Lower sorts first: how much a rider needs to know about it, most urgent at 0. */
  severity: number
  color: string
}

const ALERT_TONE_COLORS: Record<AlertPresentationTone, string> = {
  danger: Colors.danger,
  warning: Colors.warning,
  info: Colors.primary,
  muted: Colors.textSecondary,
}

/** GTFS-RT Alert.Effect, by enum name. Only reached when `alertType` is absent. */
const EFFECTS: Record<string, Presentation> = {
  NO_SERVICE:          { label: 'No service',          severity: 0,  color: Colors.danger },
  SIGNIFICANT_DELAYS:  { label: 'Significant delays',  severity: 3,  color: Colors.danger },
  REDUCED_SERVICE:     { label: 'Reduced service',     severity: 9,  color: Colors.warning },
  DETOUR:              { label: 'Detour',              severity: 7,  color: Colors.warning },
  MODIFIED_SERVICE:    { label: 'Modified service',    severity: 9,  color: Colors.warning },
  STOP_MOVED:          { label: 'Stop moved',          severity: 10, color: Colors.warning },
  ACCESSIBILITY_ISSUE: { label: 'Accessibility issue', severity: 12, color: Colors.primary },
  ADDITIONAL_SERVICE:  { label: 'Additional service',  severity: 14, color: Colors.primary },
  NO_EFFECT:           { label: 'No effect',           severity: 15, color: Colors.textSecondary },
  OTHER_EFFECT:        { label: 'Other',               severity: 15, color: Colors.textSecondary },
  UNKNOWN_EFFECT:      { label: 'Service notice',      severity: 15, color: Colors.textSecondary },
}

// protobufjs decodes an enum to its NUMBER, so a backend forwarding the raw decoded value
// lands here rather than in the name table above.
const EFFECT_BY_NUMBER: Record<string, string> = {
  '1': 'NO_SERVICE', '2': 'REDUCED_SERVICE', '3': 'SIGNIFICANT_DELAYS', '4': 'DETOUR',
  '5': 'ADDITIONAL_SERVICE', '6': 'MODIFIED_SERVICE', '7': 'OTHER_EFFECT', '8': 'UNKNOWN_EFFECT',
  '9': 'STOP_MOVED', '10': 'NO_EFFECT', '11': 'ACCESSIBILITY_ISSUE',
}

const FALLBACK: Presentation = { label: 'Service notice', severity: 15, color: Colors.textSecondary }

function presentation(alert: ServiceAlert): Presentation {
  if (alert.alertType) {
    const shared = alertTypePresentation(alert.alertType)
    return { label: shared.label, severity: shared.severity, color: ALERT_TONE_COLORS[shared.tone] }
  }

  const effect = alert.effect ?? ''
  return EFFECTS[effect.toUpperCase()] ?? EFFECTS[EFFECT_BY_NUMBER[effect]] ?? FALLBACK
}

export function alertLabel(alert: ServiceAlert): string {
  return presentation(alert).label
}

export function alertColor(alert: ServiceAlert): string {
  return presentation(alert).color
}

export function alertSeverity(alert: ServiceAlert): number {
  return presentation(alert).severity
}

/**
 * When the alert's current stretch began: the most recent start that has actually passed, 0
 * when it carries none. Future periods are skipped — an alert with a run of weekend closures
 * lists them all, and taking the latest start would date a live disruption from three weeks
 * out, rendering it as "scheduled" while it is happening.
 */
export function alertStartedAt(alert: ServiceAlert): number {
  const now = Date.now() / 1000
  let latest = 0
  for (const p of alert.activePeriods) {
    if (p.start && p.start <= now && p.start > latest) latest = p.start
  }
  return latest
}

// Subway above bus under "All", since subway is what the app leads with everywhere else.
// Station-only notices trail both, being the least actionable. A mixed subway+bus alert
// counts as subway, so it isn't pushed below the fold of the mode a rider cares about more.
function alertModeRank(alert: ServiceAlert): number {
  if (alert.routeIds.some(isSubwayRoute)) return 0
  if (alert.routeIds.some(Boolean)) return 1
  return 2
}

/**
 * In-effect first, subway above bus, most recently started at the top of each group,
 * severity breaking ties. A live list is a feed: what just broke matters more than a
 * suspension running for a week, and the color-coded labels already carry severity. Alerts
 * with no start time sink below every dated one.
 *
 * Within the upcoming set recency flips to soonest-first: for work not yet begun, "when" is
 * what a rider scans for, and a suspension three weeks out shouldn't sit above a reroute
 * tomorrow just because suspensions outrank reroutes.
 *
 * Planned work already in progress still sorts among the live alerts — `planned` says why
 * the disruption exists, `upcoming` says whether it has started.
 */
export function sortAlerts(alerts: ServiceAlert[]): ServiceAlert[] {
  return [...alerts].sort((a, b) => {
    const byUpcoming = Number(a.upcoming ?? false) - Number(b.upcoming ?? false)
    if (byUpcoming !== 0) return byUpcoming

    const byMode = alertModeRank(a) - alertModeRank(b)
    if (byMode !== 0) return byMode

    if (a.upcoming && b.upcoming) {
      const byStart = (a.startsAt ?? 0) - (b.startsAt ?? 0)
      if (byStart !== 0) return byStart
      return alertSeverity(a) - alertSeverity(b)
    }

    const byStart = alertStartedAt(b) - alertStartedAt(a)
    if (byStart !== 0) return byStart

    return alertSeverity(a) - alertSeverity(b)
  })
}

/**
 * Whether any active period overlaps the given local calendar day. `dayStartMs` is local
 * midnight; the window runs to the next. A period with no `end` runs indefinitely and one
 * with no `start` has always been in effect, so a multi-week closure matches every day it
 * spans rather than only its first.
 */
export function alertActiveOnDate(alert: ServiceAlert, dayStartMs: number): boolean {
  const dayStart = dayStartMs / 1000
  const dayEnd = dayStart + 86400
  return alert.activePeriods.some((p) => {
    const start = p.start ?? 0
    const end = p.end ?? Number.POSITIVE_INFINITY
    return start < dayEnd && end >= dayStart
  })
}

const DAY_FORMAT = new Intl.DateTimeFormat(undefined, {
  weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
})

/**
 * When this alert begins, phrased for someone reading ahead: "in 40m", "in 6h", or an
 * absolute date once a relative offset stops meaning anything.
 */
export function relativeUpcoming(alert: ServiceAlert): string {
  if (!alert.startsAt) return 'scheduled'
  const seconds = alert.startsAt - Date.now() / 1000
  if (seconds <= 0) return 'starting now'

  const mins = Math.floor(seconds / 60)
  if (mins < 60) return `in ${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 12) return `in ${hours}h`
  return DAY_FORMAT.format(new Date(alert.startsAt * 1000))
}

/** "2h ago" / "just now", or when it starts for an upcoming alert. */
export function relativeStart(alert: ServiceAlert): string {
  if (alert.upcoming) return relativeUpcoming(alert)

  const started = alertStartedAt(alert)
  if (!started) return ''
  const seconds = Date.now() / 1000 - started
  if (seconds < 0) return 'scheduled'
  if (seconds < 120) return 'just now'
  const mins = Math.floor(seconds / 60)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}
